import React, { useState, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import {
  FileText, DollarSign, Calendar, Trash2, Plus, ChevronDown, ChevronUp,
  Loader2, RefreshCw, Settings, AlertCircle, Ban, RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '~/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { LineItemRow } from './InvoiceLineItemEditor';
import { DeleteInvoiceMutation } from './useDeleteInvoice';
import { useDeleteDraftInvoices } from './useDeleteDraftInvoices';
import { AddInvoiceLineItemMutation } from './useAddInvoiceLineItem';
import { UpdateInvoiceLineItemMutation } from './useUpdateInvoiceLineItem';
import { DeleteInvoiceLineItemMutation } from './useDeleteInvoiceLineItem';
import { RegenerateTuitionInvoicesMutation } from './useRegenerateTuitionInvoices';
import { useVoidInvoice } from './useVoidInvoice';
import { useRecordManualPayment } from './useRecordManualPayment';
import { useResetInvoiceForRetry } from './useResetInvoiceForRetry';
import { PaymentSchedulePreview, calculateMonthlyPaymentCount } from '~/components/PaymentSchedulePreview';
import type {
  InvoicesSectionProps,
  FamilyEnrollment,
  StudentEnrollment,
  Invoice,
  PaymentOption,
} from './types';

const formatCurrency = (cents: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(cents / 100);
};

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return 'N/A';
  // Append T00:00:00 to treat as local time, not UTC (avoids off-by-one day bug)
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getStatusBadge = (status: string, checkoutPaymentStatus: string | null | undefined, subscriptionManaged: boolean | null | undefined, failureReason?: string | null) => {
  const variants: Record<string, 'secondary' | 'destructive' | 'default' | 'outline'> = {
    DRAFT: 'secondary',
    SCHEDULED: 'outline',
    UNPAID: 'destructive',
    PAYMENT_PROCESSING: 'secondary',
    PAYMENT_FAILED: 'destructive',
    PAID: 'default',
    VOID: 'outline'
  };

  // Show "Payment Failed" badge for failed invoices.
  if (status === 'PAYMENT_FAILED' || (status === 'UNPAID' && checkoutPaymentStatus === 'checkout_failed')) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex gap-1.5">
          <Badge variant="destructive">{status === 'PAYMENT_FAILED' ? 'FAILED' : status}</Badge>
          <Badge variant="destructive" className="bg-red-600">
            <AlertCircle className="h-3 w-3 mr-1" />
            Payment Failed
          </Badge>
        </div>
        {failureReason && (
          <p className="text-xs text-destructive">{failureReason}</p>
        )}
      </div>
    );
  }

  // Show "Processing" badge for in-flight payments.
  if (status === 'PAYMENT_PROCESSING' || (status === 'UNPAID' && checkoutPaymentStatus === 'checkout_processing')) {
    return (
      <div className="flex gap-1.5">
        <Badge variant="secondary">{status === 'PAYMENT_PROCESSING' ? 'PROCESSING' : status}</Badge>
        <Badge variant="outline" className="text-amber-600 border-amber-400 bg-amber-50">
          Processing
        </Badge>
      </div>
    );
  }

  // Show "Autopay" badge for UNPAID invoices managed by subscription
  if (status === 'UNPAID' && subscriptionManaged) {
    return (
      <div className="flex gap-1.5">
        <Badge variant="outline" className="text-muted-foreground">{status}</Badge>
        <Badge variant="outline" className="text-emerald-600 border-emerald-400 bg-emerald-50">
          Autopay
        </Badge>
      </div>
    );
  }

  return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
};

// Single Invoice Card
interface InvoiceCardProps {
  invoice: Invoice;
  studentEnrollments?: StudentEnrollment[] | null;
  onRefetch?: () => void;
  /** The old flow's Adjust Tuition is on offer for the year. */
  canAdjustTuition?: boolean;
}

function InvoiceCard({ invoice, studentEnrollments: _studentEnrollments, onRefetch, canAdjustTuition = false }: InvoiceCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [showRecordPaymentDialog, setShowRecordPaymentDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [newItem, setNewItem] = useState({ description: '', amount: '0.00', lineType: 'other' });
  const [manualPayment, setManualPayment] = useState({
    amount: (invoice.amountCents / 100).toFixed(2),
    paymentMethod: 'CHECK',
    note: ''
  });

  const [deleteInvoice, { loading: deletingInvoice }] = useMutation(DeleteInvoiceMutation, {
    onCompleted: () => onRefetch?.()
  });
  const [addLineItem, { loading: addingItem }] = useMutation(AddInvoiceLineItemMutation, {
    onCompleted: () => {
      setShowAddItemDialog(false);
      setNewItem({ description: '', amount: '0.00', lineType: 'other' });
      onRefetch?.();
    }
  });
  const [updateLineItem, { loading: updatingItem }] = useMutation(UpdateInvoiceLineItemMutation, {
    onCompleted: () => onRefetch?.()
  });
  const [deleteLineItem, { loading: deletingItem }] = useMutation(DeleteInvoiceLineItemMutation, {
    onCompleted: () => onRefetch?.()
  });
  const [voidInvoice, { loading: voidingInvoice }] = useVoidInvoice();
  const [recordManualPayment, { loading: recordingPayment }] = useRecordManualPayment();
  const [resetInvoice, { loading: resettingInvoice }] = useResetInvoiceForRetry();

  const isDraft = invoice.status === 'DRAFT';
  const isUnpaid = invoice.status === 'UNPAID';
  const isPaymentFailed = invoice.status === 'PAYMENT_FAILED' || (isUnpaid && invoice.checkoutPaymentStatus === 'checkout_failed');
  // Only allow line item editing for enrollment fee invoices (tuition invoices should use regenerate)
  const isEnrollmentFee = invoice.key === 'enrollment_fee';
  const allowLineItemEdit = isDraft && isEnrollmentFee;

  const handleDeleteInvoice = async () => {
    await deleteInvoice({ variables: { input: { invoiceId: invoice.id } } });
    setShowDeleteDialog(false);
  };

  const handleAddItem = async () => {
    await addLineItem({
      variables: {
        input: {
          invoiceId: invoice.id,
          description: newItem.description,
          amountCents: Math.round(parseFloat(newItem.amount) * 100),
          lineType: newItem.lineType
        }
      }
    });
  };

  const handleUpdateItem = async (itemId: string, updates: any) => {
    await updateLineItem({
      variables: { input: { lineItemId: itemId, ...updates } }
    });
  };

  const handleDeleteItem = async (itemId: string) => {
    await deleteLineItem({ variables: { input: { lineItemId: itemId } } });
  };

  const handleVoidInvoice = async () => {
    await voidInvoice({ variables: { input: { invoiceId: invoice.id } } });
    setShowVoidDialog(false);
    onRefetch?.();
  };

  const handleRecordPayment = async () => {
    await recordManualPayment({
      variables: {
        input: {
          invoiceId: invoice.id,
          amountCents: Math.round(parseFloat(manualPayment.amount) * 100),
          paymentMethod: manualPayment.paymentMethod,
          note: manualPayment.note || null
        }
      }
    });
    setShowRecordPaymentDialog(false);
    onRefetch?.();
  };

  const handleResetForRetry = async () => {
    await resetInvoice({ variables: { input: { invoiceId: invoice.id } } });
    setShowResetDialog(false);
    onRefetch?.();
  };

  return (
    <div className="border rounded-lg">
      {/* Invoice Header */}
      <div
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="font-medium flex items-center gap-2">
              {invoice.memo || `Invoice ${invoice.id.slice(0, 8)}`}
              {getStatusBadge(invoice.status, invoice.checkoutPaymentStatus, invoice.subscriptionManaged, invoice.autopayFailureReason)}
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Calendar className="h-3 w-3" />
              Due: {formatDate(invoice.dueDate as string | null)}
              {invoice.key && <Badge variant="outline" className="text-xs">{invoice.key}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-lg font-semibold">
            {formatCurrency(invoice.amountCents)}
          </div>
          {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t p-4 space-y-4">
          {/* Payment Failed Warning */}
          {isPaymentFailed && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-red-800 mb-1">
                <AlertCircle className="h-4 w-4" />
                Payment Failed
              </div>
              {invoice.autopayFailureReason && (
                <p className="text-red-700">{invoice.autopayFailureReason}</p>
              )}
              <p className="text-red-600 mt-1 text-xs">
                Note: ACH payment failures may incur a $4 return fee from Stripe on the school&apos;s connected account.
              </p>
            </div>
          )}

          {/* Due Date Display */}
          <div className="flex items-center gap-2">
            <Label className="w-24">Due Date:</Label>
            <span className="text-sm">{formatDate(invoice.dueDate as string | null)}</span>
          </div>

          {/* Line Items Table */}
          <div>
            <div className="text-sm font-medium mb-2">Line Items</div>
            <table className="w-full">
              <thead>
                <tr className="border-b text-muted-foreground text-sm">
                  <th className="text-left py-2">Description</th>
                  <th className="text-right py-2 w-32">Amount</th>
                  <th className="text-left py-2 w-24">Type</th>
                  {allowLineItemEdit && <th className="w-24"></th>}
                </tr>
              </thead>
              <tbody>
                {(invoice.lineItems || []).map((item) => (
                  <LineItemRow
                    key={item.id}
                    item={item}
                    isDraft={allowLineItemEdit}
                    onUpdate={handleUpdateItem}
                    onDelete={handleDeleteItem}
                    updating={updatingItem}
                    deleting={deletingItem}
                  />
                ))}
                {(!invoice.lineItems || invoice.lineItems.length === 0) && (
                  <tr>
                    <td colSpan={isDraft ? 4 : 3} className="py-4 text-center text-muted-foreground text-sm">
                      No line items
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="font-medium">
                  <td className="py-2">Total</td>
                  <td className="py-2 text-right">{formatCurrency(invoice.amountCents)}</td>
                  <td colSpan={allowLineItemEdit ? 2 : 1}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Actions */}
          {(allowLineItemEdit || isDraft) && (
            <div className="flex gap-2 pt-2 border-t">
              {allowLineItemEdit && (
                <Button variant="outline" size="sm" onClick={() => setShowAddItemDialog(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Line Item
                </Button>
              )}
              {isDraft && isEnrollmentFee && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Invoice
                </Button>
              )}
              {isDraft && !isEnrollmentFee && canAdjustTuition && (
                <p className="text-sm text-muted-foreground italic">
                  Use "Adjust Tuition" to modify tuition invoices
                </p>
              )}
            </div>
          )}

          {/* Admin actions for unpaid and failed invoices */}
          {(isUnpaid || isPaymentFailed) && (
            <div className="flex gap-2 pt-2 border-t">
              {isUnpaid && (
                <Button variant="outline" size="sm" onClick={() => setShowRecordPaymentDialog(true)}>
                  <DollarSign className="h-4 w-4 mr-1" />
                  Record Payment
                </Button>
              )}
              {isPaymentFailed && (
                <Button variant="outline" size="sm" onClick={() => setShowResetDialog(true)}>
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset for Retry
                </Button>
              )}
              {isUnpaid && (
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => setShowVoidDialog(true)}>
                  <Ban className="h-4 w-4 mr-1" />
                  Void Invoice
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Invoice Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this invoice? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteInvoice}
              disabled={deletingInvoice}
              className="bg-destructive text-destructive-foreground"
            >
              {deletingInvoice ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Line Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newItem.description}
                onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Additional enrollment fee"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newItem.amount}
                  onChange={(e) => setNewItem(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={newItem.lineType}
                  onValueChange={(val) => setNewItem(prev => ({ ...prev, lineType: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tuition">Tuition</SelectItem>
                    <SelectItem value="enrollment_fee">Enrollment Fee</SelectItem>
                    <SelectItem value="discount">Discount</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>Cancel</Button>
            <Button onClick={handleAddItem} disabled={addingItem || !newItem.description}>
              {addingItem ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Invoice Dialog */}
      <AlertDialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to void this invoice? The family will no longer be able to pay it.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVoidInvoice}
              disabled={voidingInvoice}
              className="bg-destructive text-destructive-foreground"
            >
              {voidingInvoice ? 'Voiding...' : 'Void Invoice'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset for Retry Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Invoice for Retry</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear the failed payment status and allow the family to attempt payment again
              from their enrollment portal. The previous checkout session will be discarded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetForRetry}
              disabled={resettingInvoice}
            >
              {resettingInvoice ? 'Resetting...' : 'Reset for Retry'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Record Manual Payment Dialog */}
      <Dialog open={showRecordPaymentDialog} onOpenChange={setShowRecordPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Manual Payment</DialogTitle>
            <DialogDescription>
              Record an offline payment (check, cash, etc.) for this invoice.
              This will mark the invoice as paid and advance the enrollment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={manualPayment.amount}
                onChange={(e) => setManualPayment(prev => ({ ...prev, amount: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={manualPayment.paymentMethod}
                onValueChange={(val) => setManualPayment(prev => ({ ...prev, paymentMethod: val }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHECK">Check</SelectItem>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Input
                value={manualPayment.note}
                onChange={(e) => setManualPayment(prev => ({ ...prev, note: e.target.value }))}
                placeholder="e.g., Check #1234"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordPaymentDialog(false)}>Cancel</Button>
            <Button
              onClick={handleRecordPayment}
              disabled={recordingPayment || parseFloat(manualPayment.amount) <= 0}
            >
              {recordingPayment ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <DollarSign className="h-4 w-4 mr-1" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Regenerate Tuition Dialog
interface RegenerateTuitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrollment: FamilyEnrollment | null | undefined;
  schoolYear: { startDate?: string; endDate?: string } | null;
  paymentOptions: PaymentOption[];
  onRefetch?: () => void;
}

function RegenerateTuitionDialog({ open, onOpenChange, enrollment, schoolYear, paymentOptions = [], onRefetch }: RegenerateTuitionDialogProps) {
  const tuitionInvoices = useMemo(() => {
    return (enrollment?.invoices || []).filter((i) => i.status === 'DRAFT' && i.key !== 'enrollment_fee');
  }, [enrollment?.invoices]);

  // Calculate initial values from existing invoices
  const initialValues = useMemo(() => {
    let totalTuition = 0;
    let totalDiscount = 0;
    let existingDiscountDescription = '';

    tuitionInvoices.forEach((invoice) => {
      (invoice.lineItems || []).forEach((item) => {
        if (item.lineType === 'tuition') {
          totalTuition += item.amountCents;
        } else if (item.lineType === 'discount') {
          totalDiscount += item.amountCents;
          // Try to extract discount description (remove "Discount - " prefix if present)
          if (!existingDiscountDescription && item.description) {
            existingDiscountDescription = item.description.replace(/^Discount\s*-?\s*/i, '');
          }
        }
      });
    });

    // Default start month from school year, or September as fallback
    let defaultStartMonth = 9;
    if (schoolYear?.startDate) {
      const [, month] = schoolYear.startDate.split('-').map(Number);
      defaultStartMonth = month;
    }

    // Check if existing discount description matches a payment option name
    const matchingPaymentOption = paymentOptions.find((opt) => opt.name === existingDiscountDescription);

    return {
      totalTuition,
      totalDiscount,
      scheduleType: (enrollment as Record<string, any>)?.paymentScheduleType || 'monthly',
      billingDay: (enrollment as Record<string, any>)?.billingDayOfMonth || 1,
      startMonth: defaultStartMonth,
      paymentOptionKey: matchingPaymentOption?.key || 'custom',
      discountDescription: existingDiscountDescription || ''
    };
  }, [tuitionInvoices, enrollment, schoolYear, paymentOptions]);

  const [formValues, setFormValues] = useState(initialValues);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setFormValues(initialValues);
      setError(null);
    }
  }, [open, initialValues]);

  const [regenerate, { loading }] = useMutation(RegenerateTuitionInvoicesMutation, {
    onCompleted: (data) => {
      if (data.regenerateTuitionInvoices?.userErrors?.length && data.regenerateTuitionInvoices.userErrors.length > 0) {
        setError(data.regenerateTuitionInvoices.userErrors[0].message);
      } else {
        onOpenChange(false);
        onRefetch?.();
      }
    },
    onError: (err) => {
      setError(err.message);
    }
  });

  const handleSubmit = async () => {
    setError(null);

    // Determine discount description from selected payment option or custom input
    let discountDescription = formValues.discountDescription;
    if (formValues.paymentOptionKey !== 'custom') {
      const selectedOption = paymentOptions.find((opt) => opt.key === formValues.paymentOptionKey);
      discountDescription = selectedOption?.name || '';
    }

    await regenerate({
      variables: {
        input: {
          familyEnrollmentId: enrollment!.id,
          totalTuitionCents: formValues.totalTuition,
          totalDiscountCents: formValues.totalDiscount,
          scheduleType: formValues.scheduleType,
          billingDayOfMonth: formValues.billingDay,
          startMonth: formValues.startMonth,
          discountDescription: discountDescription || null
        }
      }
    });
  };

  const netTotal = Math.max(formValues.totalTuition - formValues.totalDiscount, 0);

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const billingDayOptions = [
    { value: 1, label: '1st' },
    { value: 15, label: '15th' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Adjust Tuition Invoices
          </DialogTitle>
          <DialogDescription>
            Update the total tuition and regenerate invoices. All existing draft tuition invoices will be replaced.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Payment Option Selection */}
          <div className="space-y-2">
            <Label>Payment Schedule Option</Label>
            <Select
              value={formValues.paymentOptionKey}
              onValueChange={(val) => {
                const option = paymentOptions.find((opt) => opt.key === val);
                if (val === 'custom') {
                  setFormValues(prev => ({
                    ...prev,
                    paymentOptionKey: 'custom',
                  }));
                } else if (option) {
                  // Calculate discount based on payment option
                  const discountCents = option.discountCents || 0;
                  const discountType = option.discountType || 'per_student';
                  const numStudents = enrollment?.studentEnrollments?.length || 1;
                  // per_student = multiply by number of students, flat = use as-is
                  const calculatedDiscount = discountType === 'per_student'
                    ? discountCents * numStudents
                    : discountCents;

                  setFormValues(prev => ({
                    ...prev,
                    paymentOptionKey: val,
                    scheduleType: option.scheduleType || 'monthly',
                    billingDay: option.billingDayOfMonth || 1,
                    startMonth: option.startMonth || prev.startMonth,
                    totalDiscount: calculatedDiscount,
                    discountDescription: option.name || ''
                  }));
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select payment option..." />
              </SelectTrigger>
              <SelectContent>
                {paymentOptions.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.name} ({option.scheduleType}
                    {option.discountCents > 0 && `, $${(option.discountCents / 100).toLocaleString()} off${option.discountType === 'per_student' ? '/student' : ''}`}
                    )
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom Schedule</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Total Tuition (read-only display, comes from per-student selections) */}
          <div className="space-y-2">
            <Label>Total Tuition</Label>
            <div className="p-3 bg-muted rounded-md text-sm">
              <span className="font-semibold">${(formValues.totalTuition / 100).toLocaleString()}</span>
              <span className="text-muted-foreground ml-2">(from student tuition selections)</span>
            </div>
          </div>

          {/* Discount */}
          <div className="space-y-2">
            <Label>Discount ($)</Label>
            <Input
              type="number"
              step="0.01"
              value={(formValues.totalDiscount / 100).toFixed(2)}
              onChange={(e) => setFormValues(prev => ({
                ...prev,
                totalDiscount: Math.round(parseFloat(e.target.value || '0') * 100),
                // Switch to custom when manually editing discount
                paymentOptionKey: 'custom'
              }))}
            />
          </div>

          {/* Custom Schedule Options (only shown for custom) */}
          {formValues.paymentOptionKey === 'custom' && (
            <>
              {/* Custom Discount Description */}
              {formValues.totalDiscount > 0 && (
                <div className="space-y-2">
                  <Label>Discount Description</Label>
                  <Input
                    placeholder="e.g., Teacher Discount, Low Income Assistance"
                    value={formValues.discountDescription}
                    onChange={(e) => setFormValues(prev => ({ ...prev, discountDescription: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Will appear as "Discount - {formValues.discountDescription || 'your description'}" on invoices
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Payment Schedule</Label>
                <Select
                  value={formValues.scheduleType}
                  onValueChange={(val) => setFormValues(prev => ({ ...prev, scheduleType: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">
                      Monthly ({calculateMonthlyPaymentCount(schoolYear?.startDate, schoolYear?.endDate)} payments)
                    </SelectItem>
                    <SelectItem value="biannual">Bi-annual (2 payments)</SelectItem>
                    <SelectItem value="annual">Annual (1 payment)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Billing Day</Label>
                  <Select
                    value={String(formValues.billingDay)}
                    onValueChange={(val) => setFormValues(prev => ({ ...prev, billingDay: parseInt(val) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {billingDayOptions.map(opt => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label} of month
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Start Month</Label>
                  <Select
                    value={String(formValues.startMonth)}
                    onValueChange={(val) => setFormValues(prev => ({ ...prev, startMonth: parseInt(val) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map(opt => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Preview */}
          {netTotal > 0 && (
            <div className="pt-4 border-t">
              <PaymentSchedulePreview
                totalCents={formValues.totalTuition}
                discountCents={formValues.totalDiscount}
                scheduleType={formValues.scheduleType}
                billingDayOfMonth={formValues.billingDay}
                startMonth={formValues.startMonth}
                schoolYearStart={schoolYear?.startDate}
                schoolYearEnd={schoolYear?.endDate}
                showDiscount={true}
                className=""
                existingInvoices={[]}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Regenerate Invoices
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main InvoicesSection Component
export function InvoicesSection({ enrollment, studentEnrollments, schoolYear, paymentOptions = [], checklistEnrollmentOn = false, onRefetch }: InvoicesSectionProps) {
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showDeleteDraftsDialog, setShowDeleteDraftsDialog] = useState(false);
  const [deleteDraftsError, setDeleteDraftsError] = useState<string | null>(null);
  const [deleteDraftInvoices, { loading: deletingDrafts }] = useDeleteDraftInvoices();
  const invoices = enrollment?.invoices || [];
  // A year on its checklist gets its tuition from the tuition agreement, never the old flow's schedule.
  const onChecklist = enrollment?.accessSource === 'SET';

  // Group invoices
  const draftInvoices = invoices.filter((i) => i.status === 'DRAFT');
  const tuitionDraftInvoices = draftInvoices.filter((i) => i.key !== 'enrollment_fee');
  const enrollmentFeeInvoice = draftInvoices.find((i) => i.key === 'enrollment_fee');
  const otherInvoices = invoices.filter((i) => i.status !== 'DRAFT');

  // Check if we can regenerate tuition (all tuition invoices must be draft, and packet not sent)
  // Don't allow tuition adjustment while enrollment packet is sent but not yet submitted
  const packetPendingStates = ['PACKET_SENT', 'PACKET_VIEWED'];
  const isPacketPending = enrollment?.state ? packetPendingStates.includes(enrollment.state) : false;
  const canRegenerateTuition = !onChecklist && (tuitionDraftInvoices.length > 0 || draftInvoices.length > 0) && !isPacketPending;

  // All or nothing on the server: a refusal deletes nothing, and says why here.
  const handleDeleteAllDrafts = async () => {
    setDeleteDraftsError(null);
    try {
      const result = await deleteDraftInvoices({
        variables: { familyEnrollmentId: enrollment!.id }
      });

      const userErrors = result.data?.deleteDraftInvoices?.userErrors;
      if (userErrors && userErrors.length > 0) {
        setDeleteDraftsError(userErrors.map((error) => error.message).join(' '));
        return;
      }

      setShowDeleteDraftsDialog(false);
      onRefetch?.();
    } catch (err) {
      setDeleteDraftsError(err instanceof Error ? err.message : 'The drafts were not deleted. Please try again.');
    }
  };

  if (invoices.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Invoices
          </CardTitle>
          <CardDescription>
            No invoices have been created yet.
          </CardDescription>
        </CardHeader>
        {!checklistEnrollmentOn && (
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Invoices will be generated when the family submits their enrollment form.
            </p>
          </CardContent>
        )}
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Invoices
            </CardTitle>
            <CardDescription>
              {draftInvoices.length > 0 && (
                <span>{draftInvoices.length} draft invoice{draftInvoices.length !== 1 ? 's' : ''} pending</span>
              )}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {draftInvoices.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteDraftsDialog(true)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete All Drafts
              </Button>
            )}
            {canRegenerateTuition && (
              <Button variant="outline" size="sm" onClick={() => setShowRegenerateDialog(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Adjust Tuition
              </Button>
            )}
          </div>
        </div>
        {deleteDraftsError && (
          <p role="alert" className="text-sm text-destructive">{deleteDraftsError}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tuition Draft Invoices */}
        {tuitionDraftInvoices.length > 0 && (
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <span>Tuition Invoices (Draft)</span>
              <Badge variant="secondary">{tuitionDraftInvoices.length}</Badge>
            </div>
            {tuitionDraftInvoices.map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                studentEnrollments={studentEnrollments}
                onRefetch={onRefetch}
                canAdjustTuition={canRegenerateTuition}
              />
            ))}
          </div>
        )}

        {/* Enrollment Fee Invoice */}
        {enrollmentFeeInvoice && (
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">Enrollment Fee (Draft)</div>
            <InvoiceCard
              invoice={enrollmentFeeInvoice}
              studentEnrollments={studentEnrollments}
              onRefetch={onRefetch}
            />
          </div>
        )}

        {/* Other Invoices */}
        {otherInvoices.length > 0 && (
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">Posted Invoices</div>
            {otherInvoices.map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                studentEnrollments={studentEnrollments}
                onRefetch={onRefetch}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* Regenerate Tuition Dialog */}
      <RegenerateTuitionDialog
        open={showRegenerateDialog}
        schoolYear={schoolYear}
        paymentOptions={paymentOptions}
        onOpenChange={setShowRegenerateDialog}
        enrollment={enrollment}
        onRefetch={onRefetch}
      />

      {/* Delete All Drafts Confirmation Dialog */}
      <AlertDialog open={showDeleteDraftsDialog} onOpenChange={setShowDeleteDraftsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete All Draft Invoices?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {draftInvoices.length} draft invoice{draftInvoices.length !== 1 ? 's' : ''}.
              They will be regenerated when the reenrollment form is submitted again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllDrafts}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletingDrafts}
            >
              {deletingDrafts ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default InvoicesSection;
